import { Component, HostBinding, inject, Input, OnInit } from '@angular/core';
import { ResetFormDataAction } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { ResetCancellablePaymentsList } from '@app/finxone-web-frontend/app/lib/actions/payment.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountdownService } from '@app/finxone-web-frontend/app/lib/services/countdown-service/countdown-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import { Store } from '@ngxs/store';
import dayjs from 'dayjs';
import Handlebars from 'handlebars';
import { combineLatest, fromEvent, merge, Subject, takeUntil, tap } from 'rxjs';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-cancel-payment-template',
  templateUrl: './cancel-payment-template.component.html',
})
export class CancelPaymentTemplateComponent implements OnInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  @Input() public cancelPaymentTemplate: string | undefined;
  // Toggled via HostBinding so the element is hidden without being removed from the DOM
  @HostBinding('class.d-none') public isHidden = false;

  public compiledTemplate = '';
  private paymentCancellationSettings:
    | { isPaymentCancellationEnabled: boolean; paymentCancellationTimer: number }
    | undefined;

  private store = inject(Store);
  private countDownService = inject(CountdownService);
  private configService = inject(ConfigService);
  private accountService = inject(AccountService);
  private countriesCurrenciesService = inject(CountriesCurrenciesService);
  private iconCacheService = inject(IconCacheService);

  ngOnInit(): void {
    this.paymentCancellationSettings = this.store.selectSnapshot(
      ProjectSettingsState.getProjectSettings,
    )?.projectConfig?.paymentCancellationSettings;

    // Nothing to show if the feature is disabled in project settings
    if (!this.paymentCancellationSettings?.isPaymentCancellationEnabled) {
      this.hide();
      return;
    }

    this.setupTimer();
    // Re-sync the timer when the app returns from background (tab switch, device lock, Cordova resume)
    this.setupAppResumeDetection();
    this.subscribeToTemplateUpdates();
  }

  private subscribeToTemplateUpdates(): void {
    if (!this.cancelPaymentTemplate) return;
    const template = Handlebars.compile(this.cancelPaymentTemplate);
    combineLatest([
      this.countDownService.countdown$,
      buildHandleBarModel(
        this.store,
        this.cancelPaymentTemplate,
        this.countriesCurrenciesService,
        this.iconCacheService,
      ),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([countdown, model]) => {
        this.compiledTemplate = filterIfHandlebarNotRendered(template({ ...model, timer: countdown }));
        const formData = this.store.selectSnapshot(FormActionState.getFormActionState)?.response?.formData;
        // Hide the template when there is no jobId in the state. Since the countdown state is shared
        // across all instances of this template, cancelling a payment elsewhere (for example, by clicking
        // "Undo" on another widget or page) clears formData.jobId. This prevents a stale template from
        // remaining visible.

        // '00' is the terminal value emitted by CountdownService when the countdown expires.
        if (countdown === '00' || !formData?.jobId) {
          this.hide();
        }
      });
  }

  private hide(): void {
    // Keep the element visible in workflow/builder mode so designers can still see the widget
    if (!this.configService.getActiveConfigType()?.isWorkflow) {
      this.isHidden = true;
      this.destroy$.next();
      this.destroy$.complete();
    }
  }

  private setupTimer(): void {
    const formData = this.store.selectSnapshot(FormActionState.getFormActionState)?.response?.formData;
    if (formData?.expiryReversalAt) {
      this.startTimerIfReversalTimeNotExpired(formData.expiryReversalAt);
    }
  }

  private startTimerIfReversalTimeNotExpired(expiryReversalAt: string): void {
    const scheduledTime = dayjs(expiryReversalAt);
    const currentTime = dayjs();
    const timeDifference = scheduledTime.diff(currentTime, 'second');

    if (timeDifference > 0) {
      this.countDownService.startCountdown(timeDifference, false);
    } else {
      this.hide();
    }
  }

  public onActionClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    // Only the explicit undo button should trigger a reversal; ignore bubbled clicks from child elements
    if (target?.id !== 'undoPayment') return;

    const formData = this.store.selectSnapshot(FormActionState.getFormActionState)?.response?.formData;
    this.accountService
      .removeScheduledPayment(formData?.jobId)
      .pipe(
        tap(() => {
          this.hide();
          this.store.dispatch(new ResetFormDataAction());
        }),
      )
      .subscribe();
  }

  private setupAppResumeDetection(): void {
    // Web: Page Visibility API for tab switching
    const visibilityChange$ = fromEvent(document, 'visibilitychange').pipe(
      tap(() => {
        if (document.visibilityState === 'visible') {
          this.setupTimer();
        }
      }),
    );

    // Mobile: Custom events for app resume
    const resumeEvent$ = fromEvent(document, 'resume').pipe(tap(() => this.setupTimer()));
    // Also listen for focus events as fallback
    const focusEvent$ = fromEvent(window, 'focus').pipe(tap(() => this.setupTimer()));

    // Merge all observables in one subscription and take until destroy in order to avoid multiple subscriptions
    merge(visibilityChange$, resumeEvent$, focusEvent$).pipe(takeUntil(this.destroy$)).subscribe();
  }
}
